import { Box, Flex, IconButton, Tooltip } from '@radix-ui/themes'
import cn from 'clsx'
import { useKeenSlider } from 'keen-slider/react.js'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { RxArrowLeft, RxArrowRight } from "react-icons/rx";
import { Image, IMAGE_FILTERS } from '~/components/ui/image'
import { type ProductNormalized } from '~/models/ecommerce-providers/sylius/shop/interface/product'

/* -------------------------------------------------------------------------------------------------
 * I18next
 * -----------------------------------------------------------------------------------------------*/

enum I18NEXT {
	uiProductSlider = 'ui-product-slider',
}

/* -------------------------------------------------------------------------------------------------
 * ProductSidebar
 * -----------------------------------------------------------------------------------------------*/

type ProductSliderProps = {
	mainImage: number
	product: ProductNormalized
}

const ProductSlider: React.FC<ProductSliderProps> = ({
	mainImage,
	product,
}) => {
	const [currentSlide, setCurrentSlide] = React.useState(0)
	const [isMounted, setIsMounted] = React.useState(false)
	const sliderContainerRef = React.useRef<HTMLDivElement>(null)
	const [ref, slider] = useKeenSlider<HTMLDivElement>({
		initial: 0,
		slideChanged(slider) {
			setCurrentSlide(slider.track.details.rel)
		},
		loop: true,
		slides: { perView: 1 },
		created() {
			setIsMounted(true)
		},
	})
	const { t } = useTranslation(I18NEXT.uiProductSlider)

	// Stop the history navigation gesture on touch devices
	React.useEffect(() => {
		const preventNavigation = (event: TouchEvent) => {
			// Center point of the touch area
			const touchXPosition = event.touches[0].pageX
			// Size of the touch area
			const touchXRadius = event.touches[0].radiusX || 0

			// We set a threshold (10px) on both sizes of the screen,
			// if the touch area overlaps with the screen edges
			// it's likely to trigger the navigation. We prevent the
			// touchstart event in that case.
			if (
				touchXPosition - touchXRadius < 10 ||
				touchXPosition + touchXRadius > window.innerWidth - 10
			)
				event.preventDefault()
		}

		const slider = sliderContainerRef.current!

		slider.addEventListener('touchstart', preventNavigation)

		return () => {
			if (slider) {
				slider.removeEventListener('touchstart', preventNavigation)
			}
		}
	}, [])

	React.useEffect(() => {
		slider?.current?.moveToIdx(mainImage)
	}, [mainImage, slider])

	const prev = React.useCallback(() => slider.current?.prev(), [slider])
	const next = React.useCallback(() => slider.current?.next(), [slider])

	return (
		<Box
			overflow="hidden"
			position="relative"
			width="100%"
			height="100%"
			className="app-ImageShadowContainer select-none"
			ref={sliderContainerRef}
		>
			<Box
				ref={ref}
				position="relative"
				className={cn(
					'rounded opacity-0 transition-opacity duration-150',
					{
						'opacity-100': isMounted,
					},
					'keen-slider',
				)}
			>
				{product.images?.map(({ path }, idx) => (
					<Box key={path} id={`slider-${idx}`} className="keen-slider__slide">
						<Box>
							<Image
								alt={product.name!}
								className="app-ImageShadow h-full max-h-[500px] w-full object-scale-down p-12"
								filter={IMAGE_FILTERS.sliderFilter}
								height={1000}
								src={path!}
								width={1000}
								loading={!idx ? 'eager' : 'lazy'}
							/>
						</Box>
					</Box>
				))}
			</Box>
			{!!((product.images?.length || 0) > 1 && slider) && (
				<Flex
					justify="center"
					align="center"
					gapX="5"
					position="relative"
					mt={{ lg: '-5' }}
					className="z-10"
				>
					<Tooltip content={t('previousImage')}>
						<IconButton size="2" radius="full" onClick={prev}>
							<RxArrowLeft width={20} height={20} className="fill-accent-1" />
						</IconButton>
					</Tooltip>
					<Flex asChild align="center" gapX="3">
						<ul>
							{product.images?.map((_, i) => (
								<li key={i}>
									<button
										className={cn(
											'h-4 w-4 rounded-full',
											'transition duration-300 ease-in-out',
											{
												'bg-accent-6': currentSlide !== i,
												'bg-accent-10': currentSlide === i,
											},
										)}
										onClick={() => slider.current?.moveToIdx(i)}
									/>
								</li>
							))}
						</ul>
					</Flex>
					<Tooltip content={t('nextImage')}>
						<IconButton size="2" radius="full" onClick={next}>
							<RxArrowRight width={20} height={20} className="fill-accent-1" />
						</IconButton>
					</Tooltip>
				</Flex>
			)}
		</Box>
	)
}
ProductSlider.displayName = 'ProductSlider'

export { ProductSlider, I18NEXT as PRODUCT_SLIDER_I18NEXT }
